/**
 * The numbers behind a chart, as a table.
 *
 * ## Why this exists
 *
 * Every chart in the report is an `<svg role="img">` with ONE `aria-label`. That is correct for a
 * graphic, but it means the data inside it — 36 IRR cells, six category scores, the lender hurdles —
 * reaches nobody who is not looking at pixels. A screen reader hears "IRR sensitivity heatmap" and
 * gets none of the underwriting. So does anyone copying figures into a model, and so does a client's
 * accessibility review.
 *
 * A table view is the standard answer, and the rule it follows is that a chart's data must never be
 * GATED behind an interaction: the visual is the fast path, the table is the complete one, and both
 * carry the same numbers. It is collapsed by default (`<details>`) so it costs a client nothing, and
 * forced open in print so an exported PDF carries the figures.
 *
 * Values are rendered as React children, so text is escaped — a label that arrives from an extracted
 * document is data, never markup.
 */

import { type ReactNode } from 'react';
import { usePrintExpanded } from './usePrintExpanded.js';

export interface ChartTableProps {
  /** What the table is — announced to a screen reader and shown as the disclosure label. */
  caption: string;
  /** Column headers. The first is the row-header column when `rowHeaders` is set. */
  columns: string[];
  rows: (string | number)[][];
  /**
   * Treat the first cell of every row as a row header (`<th scope="row">`) — right for a matrix, where
   * a cell is identified by BOTH its row and its column.
   */
  rowHeaders?: boolean;
  /** Optional note under the table (units, a caveat). */
  note?: ReactNode;
}

export function ChartTable({ caption, columns, rows, rowHeaders, note }: ChartTableProps) {
  // Forced open in print — see `usePrintExpanded` for why this cannot be a stylesheet rule.
  const ref = usePrintExpanded();

  if (rows.length === 0) return null;
  return (
    <details className="ctbl" ref={ref}>
      <summary className="ctbl__summary">View the numbers</summary>
      <div className="ctbl__scroll">
        <table className="ctbl__table">
          <caption className="ctbl__caption">{caption}</caption>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={c} scope="col" className={i === 0 && rowHeaders ? 'ctbl__corner' : ''}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((value, c) =>
                  c === 0 && rowHeaders ? (
                    <th key={c} scope="row">
                      {value}
                    </th>
                  ) : (
                    <td key={c}>{value}</td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {note && <p className="ctbl__note">{note}</p>}
    </details>
  );
}
